import { useCallback, useEffect, useRef, useState } from 'react'
import { Icon } from '@iconify/react'
import { useCampaignTotpStepUp } from '@/contexts/CampaignTotpStepUpContext'
import type { CampaignEditorSaveTask } from '@/pages/CampanhasPage/editor/campaignEditorSaveTask'
import {
  aiError,
  aiMoney,
  getAiAdmin,
  getAiCampaign,
  saveAiAdmin,
  saveAiCampaign,
  type AiAdminSettings,
  type AiCampaignSettings,
} from './api'
import styles from './CampaignAi.module.css'

export function AiAdminPage() {
  const { runWithStepUp } = useCampaignTotpStepUp()
  const [data, setData] = useState<AiAdminSettings | null>(null)
  const [apiKey, setApiKey] = useState('')
  const [editingApiKey, setEditingApiKey] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [busy, setBusy] = useState(false)
  const [reload, setReload] = useState(0)
  const saving = useRef(false)
  useEffect(() => {
    const controller = new AbortController()
    void getAiAdmin(controller.signal)
      .then(setData)
      .catch((e: unknown) => {
        if (!controller.signal.aborted) setError(aiError(e))
      })
    return () => controller.abort()
  }, [reload])
  async function save() {
    if (!data || saving.current) return
    saving.current = true
    setBusy(true)
    setError('')
    setNotice('')
    try {
      const result = await runWithStepUp(() => saveAiAdmin(data, apiKey))
      if (result) {
        setData(result)
        setApiKey('')
        setEditingApiKey(false)
        setNotice('Configurações da inteligência artificial salvas.')
      }
    } catch (e) {
      setError(aiError(e))
    } finally {
      saving.current = false
      setBusy(false)
    }
  }
  const total =
    data?.splits.reduce((sum, split) => sum + Number(split.percentage), 0) ?? 0
  return (
    <div className={styles.settings}>
      <header className={styles.hero}>
        <span className={styles.robot}>
          <Icon icon="mdi:robot-happy-outline" width={34} />
        </span>
        <div>
          <span className={styles.eyebrow}>ADMINISTRAÇÃO · ACESSO PROTEGIDO</span>
          <h1>Inteligência artificial</h1>
          <p>Conversas que aproximam. Créditos que ajudam.</p>
        </div>
      </header>
      {error && (
        <div className={styles.error} role="alert">
          {error}{' '}
          <button
            type="button"
            onClick={() => {
              setError('')
              setReload((v) => v + 1)
            }}
          >
            Recarregar configurações
          </button>
        </div>
      )}
      {notice && (
        <p className={styles.notice} role="status">
          {notice}
        </p>
      )}
      {!data ? (
        <p role="status">Carregando configurações…</p>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            void save()
          }}
        >
          <fieldset disabled={busy} className={styles.fieldset}>
            <section className={styles.card}>
              <div className={styles.sectionHeading}>
                <div>
                  <h2>Conexão e disponibilidade</h2>
                  <p>O token é criptografado e nunca é enviado aos participantes.</p>
                </div>
                <label className={styles.toggle}>
                  <input
                    type="checkbox"
                    checked={data.enabled}
                    onChange={(e) => setData({ ...data, enabled: e.target.checked })}
                  />{' '}
                  Ativar IA
                </label>
              </div>
              <div className={styles.formGrid}>
                <label>
                  Token da OpenAI
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={
                      data.has_api_key && !editingApiKey && !apiKey ? '********' : apiKey
                    }
                    maxLength={512}
                    onChange={(e) => setApiKey(e.target.value)}
                    onFocus={() => setEditingApiKey(true)}
                    onBlur={() => {
                      if (!apiKey) setEditingApiKey(false)
                    }}
                    placeholder={
                      data.has_api_key
                        ? 'Token cadastrado · preencha apenas para substituir'
                        : 'Cole seu token de API'
                    }
                  />
                  <small>
                    {data.has_api_key
                      ? 'Token salvo e protegido. Clique no campo para substituir.'
                      : 'Cadastre um token antes de ativar.'}
                  </small>
                </label>
                <label>
                  Modelo de texto
                  <input
                    required
                    maxLength={80}
                    pattern="[a-zA-Z0-9._\-]+"
                    value={data.model}
                    onChange={(e) => setData({ ...data, model: e.target.value })}
                  />
                  <small>Identificador de modelo compatível com a Responses API.</small>
                </label>
                <label>
                  Preço mínimo por crédito (R$)
                  <input
                    type="number"
                    min="0.10"
                    max="1000"
                    step="0.01"
                    required
                    value={data.minimum_credit_price_cents / 100}
                    onChange={(e) =>
                      setData({
                        ...data,
                        minimum_credit_price_cents: Math.round(
                          Number(e.target.value) * 100
                        ),
                      })
                    }
                  />
                  <small>Um crédito permite uma resposta concluída.</small>
                </label>
              </div>
            </section>
            <section className={styles.card}>
              <h2>Instruções gerais e limites</h2>
              <p>
                Estas regras têm prioridade sobre o contexto informado pelas campanhas. O
                chat oferece somente texto.
              </p>
              <label>
                Orientações da plataforma
                <textarea
                  rows={8}
                  required
                  maxLength={12000}
                  value={data.instructions}
                  onChange={(e) => setData({ ...data, instructions: e.target.value })}
                />
                <small>
                  {data.instructions.length.toLocaleString('pt-BR')} / 12.000 caracteres
                </small>
              </label>
            </section>
            <section className={styles.card}>
              <div className={styles.sectionHeading}>
                <div>
                  <h2>Distribuição das compras</h2>
                  <p>
                    O vendedor principal é sempre o da campanha. Configure os demais
                    beneficiários abaixo.
                  </p>
                </div>
                <strong className={styles.chip}>
                  {(100 - total).toLocaleString('pt-BR')}% para a entidade
                </strong>
              </div>
              {data.splits.map((split, i) => (
                <div className={styles.splitRow} key={i}>
                  <label>
                    Beneficiário
                    <input
                      required
                      maxLength={80}
                      value={split.name}
                      onChange={(e) =>
                        setData({
                          ...data,
                          splits: data.splits.map((s, j) =>
                            j === i ? { ...s, name: e.target.value } : s
                          ),
                        })
                      }
                    />
                  </label>
                  <label>
                    ID do vendedor
                    <input
                      required
                      maxLength={100}
                      value={split.user_id}
                      onChange={(e) =>
                        setData({
                          ...data,
                          splits: data.splits.map((s, j) =>
                            j === i ? { ...s, user_id: e.target.value } : s
                          ),
                        })
                      }
                    />
                  </label>
                  <label>
                    Percentual (%)
                    <input
                      type="number"
                      required
                      min="0.1"
                      max="80"
                      step="0.01"
                      value={split.percentage}
                      onChange={(e) =>
                        setData({
                          ...data,
                          splits: data.splits.map((s, j) =>
                            j === i ? { ...s, percentage: Number(e.target.value) } : s
                          ),
                        })
                      }
                    />
                  </label>
                  <button
                    type="button"
                    className={styles.iconButton}
                    aria-label={`Remover split ${i + 1}`}
                    onClick={() =>
                      setData({ ...data, splits: data.splits.filter((_, j) => i !== j) })
                    }
                  >
                    <Icon icon="mdi:trash-can-outline" width={22} />
                  </button>
                </div>
              ))}
              <button
                type="button"
                className={styles.secondary}
                disabled={data.splits.length >= 10}
                onClick={() =>
                  setData({
                    ...data,
                    splits: [...data.splits, { name: '', user_id: '', percentage: 10 }],
                  })
                }
              >
                + Adicionar beneficiário
              </button>
              <p className={styles.muted}>
                Até 80% em splits. A entidade recebe o restante, antes das taxas do
                gateway. Sem beneficiários, 100% ficam com o vendedor da campanha.
              </p>
            </section>
            <div className={styles.saveBar}>
              <p>
                Histórico de 90 dias · créditos sem vencimento · falhas devolvem o crédito
              </p>
              <button className={styles.primary} disabled={total > 80}>
                {busy ? 'Salvando…' : 'Salvar configurações'}
              </button>
            </div>
          </fieldset>
        </form>
      )}
    </div>
  )
}

export function CampaignAiTab({
  campaignId,
  onSaveStateChange,
  onSaved,
}: {
  campaignId: number
  onSaveStateChange?: (task: CampaignEditorSaveTask | null) => void
  onSaved?: () => void
}) {
  const { runWithStepUp } = useCampaignTotpStepUp()
  const [data, setData] = useState<AiCampaignSettings | null>(null)
  const [snapshot, setSnapshot] = useState('')
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [busy, setBusy] = useState(false)
  const [reload, setReload] = useState(0)
  const saving = useRef(false)
  const saveRef = useRef<() => Promise<boolean>>(async () => false)
  const saveTrigger = useCallback(() => saveRef.current(), [])
  const changed = data !== null && JSON.stringify(data) !== snapshot
  useEffect(() => {
    const controller = new AbortController()
    void getAiCampaign(campaignId, controller.signal)
      .then((result) => {
        if (!controller.signal.aborted) {
          setData(result)
          setSnapshot(JSON.stringify(result))
        }
      })
      .catch((e: unknown) => {
        if (!controller.signal.aborted) setError(aiError(e))
      })
    return () => controller.abort()
  }, [campaignId, reload])
  useEffect(() => {
    onSaveStateChange?.({ hasChanges: changed, saving: busy, save: saveTrigger })
  }, [changed, busy, saveTrigger, onSaveStateChange])
  useEffect(() => () => onSaveStateChange?.(null), [onSaveStateChange])
  useEffect(() => {
    saveRef.current = async () => {
      if (!data || saving.current) return false
      saving.current = true
      setBusy(true)
      setError('')
      setNotice('')
      try {
        const result = await runWithStepUp(() => saveAiCampaign(campaignId, data))
        if (!result) return false
        setData(result)
        setSnapshot(JSON.stringify(result))
        setNotice('Assistente atualizado.')
        onSaved?.()
        return true
      } catch (e) {
        setError(aiError(e))
        return false
      } finally {
        saving.current = false
        setBusy(false)
      }
    }
  }, [campaignId, data, onSaved, runWithStepUp])
  return (
    <div className={styles.settings}>
      <header className={styles.hero}>
        <span className={styles.robot}>
          <Icon icon="mdi:robot-happy-outline" width={34} />
        </span>
        <div>
          <span className={styles.eyebrow}>UM NOVO JEITO DE PARTICIPAR</span>
          <h2>O assistente da sua campanha</h2>
          <p>Apresente sua causa, tire dúvidas e transforme conversas em apoio.</p>
        </div>
      </header>
      {error && (
        <p role="alert" className={styles.error}>
          {error}{' '}
          <button type="button" onClick={() => setReload((v) => v + 1)}>
            Recarregar
          </button>
        </p>
      )}
      {notice && (
        <p role="status" className={styles.notice}>
          {notice}
        </p>
      )}
      {!data ? (
        <p role="status">Carregando assistente…</p>
      ) : (
        <fieldset disabled={busy} className={styles.fieldset}>
          {!data.platform_ready && (
            <p className={styles.notice}>
              Você já pode configurar o assistente. A disponibilização depende da ativação
              da IA pela administração.
            </p>
          )}
          <section className={styles.card}>
            <div className={styles.sectionHeading}>
              <h3>Identidade e créditos</h3>
              <label className={styles.toggle}>
                <input
                  type="checkbox"
                  checked={data.enabled}
                  onChange={(e) => setData({ ...data, enabled: e.target.checked })}
                />{' '}
                Ativar nesta campanha
              </label>
            </div>
            <div className={styles.formGrid}>
              <label>
                Nome do assistente
                <input
                  maxLength={60}
                  value={data.name}
                  onChange={(e) => setData({ ...data, name: e.target.value })}
                  placeholder="Ex.: Amigo da causa"
                />
              </label>
              <label>
                Preço por crédito (R$)
                <input
                  type="number"
                  min={data.minimum_credit_price_cents / 100}
                  max={1000}
                  step="0.01"
                  value={data.credit_price_cents / 100}
                  onChange={(e) =>
                    setData({
                      ...data,
                      credit_price_cents: Math.round(Number(e.target.value) * 100),
                    })
                  }
                />
                <small>
                  Mínimo da plataforma: {aiMoney(data.minimum_credit_price_cents)}.
                </small>
              </label>
            </div>
            <label>
              Mensagem de boas-vindas
              <textarea
                rows={2}
                maxLength={300}
                value={data.welcome_message ?? ''}
                onChange={(e) => setData({ ...data, welcome_message: e.target.value })}
              />
            </label>
          </section>
          <section className={styles.card}>
            <h3>Conhecimento da campanha</h3>
            <p>
              Conte a história da entidade, seus objetivos, regras de participação e
              canais de atendimento. Essas informações serão usadas como contexto nas
              respostas.
            </p>
            <label>
              Texto de orientação
              <textarea
                rows={10}
                maxLength={16000}
                value={data.instructions ?? ''}
                onChange={(e) => setData({ ...data, instructions: e.target.value })}
                placeholder="Nossa entidade atua em… A campanha tem como objetivo… Para falar com a equipe…"
              />
              <small>
                {(data.instructions?.length ?? 0).toLocaleString('pt-BR')} / 16.000
                caracteres. Use informações públicas e mantenha o texto atualizado.
              </small>
            </label>
          </section>
          <aside className={styles.preview}>
            <Icon icon="mdi:robot-happy-outline" width={30} />
            <div>
              <strong>{data.name || 'Seu assistente'}</strong>
              <p>{data.welcome_message || 'Olá! Como posso ajudar?'}</p>
              <small>
                1 resposta = 1 crédito · {aiMoney(data.credit_price_cents)} por crédito
              </small>
            </div>
          </aside>
          <div className={styles.saveBar}>
            <p>
              A compra também apoia sua entidade e concede números conforme o valor e as
              regras da campanha. O participante vê os valores antes de pagar.
            </p>
            <button
              type="button"
              className={styles.primary}
              disabled={!changed}
              onClick={() => void saveTrigger()}
            >
              {busy ? 'Salvando…' : 'Salvar assistente'}
            </button>
          </div>
        </fieldset>
      )}
    </div>
  )
}
